import { createAsyncThunk } from '@reduxjs/toolkit'
import { taskTrackerApi } from '../../api/taskTracker.api'
import type { TaskTrackerBoardSticker } from '../domain.types'
import { getErrorMessage } from '../shared/thunk.utils'

export type FetchBoardStickersTabArg =
  | number
  | { boardId: number; force?: boolean; silent?: boolean }

export type ReorderBoardStickerThunkPayload = {
  boardId: number
  activeFieldSettingId: number
  position: number
  reorderedBoardStickers: TaskTrackerBoardSticker[]
  previousBoardStickers: TaskTrackerBoardSticker[]
}

export const getFetchBoardStickersTabBoardId = (
  arg: FetchBoardStickersTabArg,
): number => (typeof arg === 'number' ? arg : arg.boardId)

const shouldSilentFetchBoardStickersTab = (arg: FetchBoardStickersTabArg) =>
  typeof arg === 'object' && Boolean(arg.silent)

export const fetchBoardStickersTabThunk = createAsyncThunk(
  'taskTracker/fetchBoardStickersTab',
  async (arg: FetchBoardStickersTabArg, { rejectWithValue }) => {
    const boardId = getFetchBoardStickersTabBoardId(arg)
    const silent = shouldSilentFetchBoardStickersTab(arg)

    try {
      const boardData = await taskTrackerApi.getBoardStickers(boardId)

      return {
        boardId,
        stickers: boardData.stickers,
        silent,
      }
    } catch (error: unknown) {
      return rejectWithValue({
        boardId,
        silent,
        error: getErrorMessage(error, 'Не удалось загрузить стикеры'),
      })
    }
  },
)

export const setBoardStickerVisibilityThunk = createAsyncThunk(
  'taskTracker/setBoardStickerVisibility',
  async (
    payload: { boardId: number; fieldSettingId: number; isVisible: boolean },
    { rejectWithValue },
  ) => {
    try {
      const boardData = await taskTrackerApi.setBoardStickerVisibility(
        payload.boardId,
        {
          fieldSettingId: payload.fieldSettingId,
          isVisible: payload.isVisible,
        },
      )
      return { boardId: payload.boardId, stickers: boardData.stickers }
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Не удалось изменить видимость'))
    }
  },
)

export const setBoardStickerRequiredThunk = createAsyncThunk(
  'taskTracker/setBoardStickerRequired',
  async (
    payload: { boardId: number; fieldSettingId: number; isRequired: boolean },
    { rejectWithValue },
  ) => {
    try {
      const boardData = await taskTrackerApi.setBoardStickerRequired(
        payload.boardId,
        {
          fieldSettingId: payload.fieldSettingId,
          isRequired: payload.isRequired,
        },
      )
      return { boardId: payload.boardId, stickers: boardData.stickers }
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось изменить обязательность'),
      )
    }
  },
)

export const reorderBoardStickerThunk = createAsyncThunk(
  'taskTracker/reorderBoardSticker',
  async (payload: ReorderBoardStickerThunkPayload, { rejectWithValue }) => {
    try {
      const boardData = await taskTrackerApi.setBoardStickerPosition(
        payload.boardId,
        {
          fieldSettingId: payload.activeFieldSettingId,
          position: payload.position,
        },
      )
      return {
        boardId: payload.boardId,
        stickers: boardData.stickers,
        previousBoardStickers: payload.previousBoardStickers,
      }
    } catch (error: unknown) {
      return rejectWithValue({
        boardId: payload.boardId,
        previousBoardStickers: payload.previousBoardStickers,
        error: getErrorMessage(error, 'Не удалось изменить порядок стикера'),
      })
    }
  },
)
